import {damsFetch} from "../app/damsFetch";
import mime from "react-native-mime-types";
import {clientLog} from "../clientLog";
import {ObjectDefinitions} from "../objectdefinitions/ObjectDefinitions";

/**
 * Builds a URL for retrieving a document from the DAMS.
 *
 * @param {{collectionId: string, dmsId: string, isImage?: boolean}} options - Options for building the URL.
 * @param {string} options.collectionId - The ID of the collection that contains the document.
 * @param {string} options.dmsId - The ID of the document.
 * @param {boolean} [options.isImage=true] - If true, the document is an image. If false, the document is a file.
 * @returns {Promise<string>} A promise for the URL.
 */
export const buildGetUrl = ({collectionId, dmsId, isImage = true}) => {
    const params = {
        collectionId: collectionId,
        multimediaId: dmsId,
    };

    const docType = isImage ? "image" : "file";

    return damsFetch(`/dms/build-get-url/${docType}`, {
        method: "POST",
        body: JSON.stringify(params),
    });
};

/**
 * Builds a URL for streaming a document from the DAMS.
 *
 * @param {{collectionId: string, dmsId: string}} options - Options for building the URL.
 * @param {string} options.collectionId - The ID of the collection that contains the document.
 * @param {string} options.dmsId - The ID of the document.
 *
 * @return {Promise<string>} A promise for the URL.
 */
export const buildStreamUrl = ({collectionId, dmsId}) => {
    const params = {
        collectionId: collectionId,
        multimediaId: dmsId,
    };

    return damsFetch(`/dms/build-stream-url/`, {
        method: "POST",
        body: JSON.stringify(params),
    });
};

/**
 * Orders an upload URL for a file from the DAMS.
 *
 * @param {string} collectionId - The ID of the collection that contains the document.
 * @param {File} file - The file to upload.
 *
 * @return {Promise<string>} A promise for the ordered upload URL.
 */
export const orderUploadUrl = async (collectionId, file) => {
    const {getMimeTypeFromFilename} = ObjectDefinitions();
    let mimeType = file.type;
    if (!file.type || file.type === '') {
        mimeType = mime.lookup(file.name);
        if (!mimeType) {
            mimeType = await getMimeTypeFromFilename(file.name)
        }
    }
    const params = {
        collectionId: collectionId,
        filename: file.name,
        mediaType: mimeType,
    };
    return damsFetch(`/dms/order-upload-url/`, {
        method: "POST",
        body: JSON.stringify(params),
    });
};

/**
 * Orders a playback link for a media file from the DAMS.
 *
 * @param {string} collectionId - The ID of the collection that contains the document.
 * @param {string} dmsId - The ID of the document.
 * @param {string} mediaFormat - The format to use for the playback link.
 *
 * @return {Promise<string>} A promise for the ordered playback link.
 */
export const orderPlaybackLink = (collectionId, dmsId, mediaFormat = '') => {
    const params = {
        collectionId: collectionId,
        multimediaId: dmsId,
        mediaFormat: mediaFormat
    };
    return damsFetch(`/dms/order-playback-link/`, {
        method: "POST",
        body: JSON.stringify(params),
    }).catch((e) => {
        clientLog('error', e);
    });
};

/**
 * Orders a download URL for a media file from the DAMS.
 *
 * @param {{collectionId: string, dmsId: string, filename: string}} options - Options for ordering the download URL.
 * @param {string} options.collectionId - The ID of the collection that contains the document.
 * @param {string} options.dmsId - The ID of the document.
 * @param {string} options.filename - The name of the file to download.
 *
 * @return {Promise<string>} A promise for the ordered download URL.
 */
export const orderDownloadUrl = ({collectionId, dmsId, filename}) => {
    const url = `/dms/order-download-url/`;
    const params = {
        collectionId: collectionId,
        multimediaId: dmsId,
        filename: filename
    };
    return damsFetch(url, {
        method: "POST",
        body: JSON.stringify(params),
    });
};

/**
 * Extracts metadata for a media file from the DAMS.
 *
 * @param {string} collectionId - The ID of the collection that contains the document.
 * @param {string} dmsId - The ID of the document.
 * @param {boolean} [regenerate=false] - If true, the metadata will be regenerated from the file.
 *
 * @return {Promise<Object[]>} A promise that resolves to an array of objects,
 *   where each object contains metadata for the media file.
 */
export const extractMediaMeta = (collectionId, dmsId, regenerate) => {
    const params = {
        collectionId: collectionId,
        multimediaId: dmsId,
        regenerate: regenerate ? regenerate : false
    };

    return damsFetch(`/dms/extract-media-meta/`, {
        method: "POST",
        body: JSON.stringify(params),
    }, true);
};

